// compliance-window.ts — the EMPTY Compliance Window shell.
//
// This is chrome, not gameplay. The rule banner, patience bar, and do-re-mi ladder are inert
// placeholders — the loop that drives them (rule generation, patience curve, combo scoring,
// registers) is LOCKED in BRIEF.md and is Evening-1 work, deliberately not built here.
//
// What IS wired is the one seam the juice rig needs: type a word → validate against ENABLE →
// render it as letter tiles → emit the matching bus event. That gives the rig a real, on-theme test
// event (a valid word) plus the reject and the profanity-contempt paths, without touching game
// design. Effects hang off the bus; this file never animates anything itself.

import { bus } from "../core/bus";
import { validate, dictMeta } from "../core/dictionary";
import { unlockAudio, setMuted, blip } from "../juice/audio";
import { isCalm, setCalm } from "../juice/a11y";

const LADDER = ["do", "re", "mi", "fa", "so", "la", "ti"];

export interface ComplianceWindow {
  submit: (word: string) => void;
  showPhrase: (phrase: string, message: string) => HTMLElement[];
  stampInput: () => void;
}

export function mountComplianceWindow(root: HTMLElement): ComplianceWindow {
  const m = dictMeta();
  root.innerHTML = `
    <div id="stage" class="stage">
      <header class="topbar">
        <span class="brand">DULY&nbsp;NOTED</span>
        <span class="ctag">Compliance&nbsp;Window</span>
        <span class="version-slot"></span>
      </header>

      <section class="window" aria-label="Compliance Window (empty)">
        <div class="rule">
          <span class="rule-tag">RULE</span>
          <span class="rule-body">— <em>(empty · rule generation is Evening-1 work, locked in BRIEF.md)</em></span>
        </div>

        <div class="patience" title="placeholder — patience curve not built">
          <span class="patience-label">PATIENCE</span>
          <div class="patience-track"><div class="patience-fill"></div></div>
        </div>

        <div class="ladder" aria-hidden="true">
          ${LADDER.map((n) => `<span class="rung">${n}</span>`).join("")}
        </div>

        <div class="utterance" id="utterance" aria-live="polite"></div>

        <div class="cw-input-wrap cw-pre-stamp" id="cw-input-wrap">
          <form id="submit-form" autocomplete="off">
            <input id="word-input" name="word" type="text" inputmode="latin"
                   placeholder="type a word, press ↵ — the rig responds"
                   spellcheck="false" />
          </form>
        </div>

        <div class="status" id="status">
          ${m.mode === "pilot"
            ? `pilot dictionary: ${m.words} words (fast) · <a href="?dict=full">?dict=full</a> for all ${m.fullWords.toLocaleString()}`
            : `full dictionary: ${m.words.toLocaleString()} words`} ·
          ${m.slursStripped} slurs stripped · ${m.profanityFlagged} profanity flags
        </div>
      </section>

      <footer class="rig-readout">
        <span>rig tally (chars, effect demo — not a score): <strong id="rig-tally">0</strong></span>
        <div class="footer-toggles">
          <button id="motion-toggle" class="sound-toggle" data-tip="Toggle the whole-screen SHAKE. Everything else — tile pops, particles, poses, sound — plays either way.">🌿 Calm</button>
          <button id="sound-toggle" class="sound-toggle on" title="Toggle sound">🔊 Sound</button>
        </div>
      </footer>
    </div>
  `;

  const form = root.querySelector<HTMLFormElement>("#submit-form")!;
  const input = root.querySelector<HTMLInputElement>("#word-input")!;
  const utterance = root.querySelector<HTMLDivElement>("#utterance")!;
  const status = root.querySelector<HTMLDivElement>("#status")!;

  /** Render the word as letter tiles; return the tile elements for effects to animate. */
  function renderTiles(word: string): HTMLElement[] {
    utterance.className = "utterance";
    utterance.innerHTML = "";
    return [...word].map((ch) => {
      const tile = document.createElement("span");
      tile.className = "tile";
      tile.textContent = ch;
      utterance.appendChild(tile);
      return tile;
    });
  }

  /** The one seam: validate + emit. No scoring, no rules — just routes to a bus event. */
  function submit(raw: string): void {
    const word = raw.trim().toLowerCase();
    if (!word) return;
    unlockAudio();
    const at = performance.now();
    bus.emit("word:submitted", { word, at });
    const tiles = renderTiles(word);
    const { valid, flagged } = validate(word);

    if (!valid) {
      utterance.classList.add("is-invalid");
      status.textContent = `“${word}” — not in the dictionary.`;
      bus.emit("word:invalid", { word, tiles, at });
    } else if (flagged) {
      utterance.classList.add("is-flagged");
      status.textContent = `“${word}” — noted. Accepted, scored zero. (LDNOOBW flag)`;
      bus.emit("word:flagged", { word, tiles, at });
    } else {
      status.textContent = `“${word}” — valid (${word.length}).`;
      bus.emit("word:valid", { word, tiles, flagged: false, at });
    }
  }

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    submit(input.value);
    input.value = "";
  });

  // The type-a-word field's space is reserved from the start (cw-pre-stamp = invisible but sized), so
  // the window never resizes. On the second fired word the intro STAMPS it in: it slams down from
  // oversized, lands slightly askew, straightens, and thuds — like a rubber stamp hitting the paper.
  const inputWrap = root.querySelector<HTMLElement>("#cw-input-wrap")!;
  let stamped = false;
  function stampInput(): void {
    if (stamped) return;
    stamped = true;
    unlockAudio();
    inputWrap.classList.remove("cw-pre-stamp");
    void inputWrap.offsetWidth; // reflow so the stamp animation runs from its start frame
    inputWrap.classList.add("cw-stamp");
    blip({ freq: 88, type: "square", volume: 0.42, durationMs: 120 }); // the stamp thud
    window.setTimeout(() => input.focus(), 320);
  }

  // Sound toggle (default ON) — lives in the game footer.
  const soundBtn = root.querySelector<HTMLButtonElement>("#sound-toggle")!;
  soundBtn.addEventListener("click", () => {
    unlockAudio();
    const on = soundBtn.classList.toggle("on");
    setMuted(!on);
    soundBtn.textContent = on ? "🔊 Sound" : "🔇 Muted";
  });

  // Shake toggle (default = calm/shake-off) — sits next to Sound. `.on` = shake allowed (bright, like
  // Sound); off = calm. Only the whole-screen shake is affected; all other juice always plays.
  const motionBtn = root.querySelector<HTMLButtonElement>("#motion-toggle")!;
  const paintMotion = () => {
    const shakeOn = !isCalm();
    motionBtn.classList.toggle("on", shakeOn);
    motionBtn.textContent = shakeOn ? "🎢 Motion" : "🌿 Calm";
  };
  paintMotion();
  motionBtn.addEventListener("click", () => {
    setCalm(!isCalm());
    paintMotion();
  });

  /** A splash: render a multi-word phrase as tiles and fire the full juice + personality dance.
   *  Bypasses the dictionary (it's a greeting, not a play). Spaces render as gaps. */
  function showPhrase(phrase: string, message: string): HTMLElement[] {
    unlockAudio();
    utterance.className = "utterance is-phrase";
    utterance.innerHTML = "";
    const tiles: HTMLElement[] = [];
    // `\n` forces a new line; within a line, words are grouped so wrapping never breaks a word.
    for (const line of phrase.split("\n")) {
      const lineEl = document.createElement("div");
      lineEl.className = "utter-line";
      for (const word of line.split(" ")) {
        const wordEl = document.createElement("span");
        wordEl.className = "utter-word";
        for (const ch of word) {
          const tile = document.createElement("span");
          tile.className = "tile";
          tile.textContent = ch;
          wordEl.appendChild(tile);
          tiles.push(tile);
        }
        lineEl.appendChild(wordEl);
      }
      utterance.appendChild(lineEl);
    }
    status.textContent = message;
    bus.emit("word:valid", {
      word: phrase.replace(/ /g, "").toLowerCase(),
      tiles,
      flagged: false,
      at: performance.now(),
    });
    return tiles;
  }

  return { submit, showPhrase, stampInput };
}
